import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';

/**
 * Retrying a 4xx is pointless — the request was wrong, not unlucky — and
 * retrying a 401 races the refresh interceptor. Only server and network faults
 * are worth a second attempt.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

/**
 * Everything one session put in memory, forgotten — the whole of it, not a
 * list of keys someone has to remember to extend.
 *
 * A cache entry is keyed by endpoint and parameters and **never by who
 * asked**, so `GET /users` answered for a platform administrator and the same
 * call answered for a clinic are the same entry. The trap is that this is not
 * a flash of stale rows: `staleTime` makes the entry fresh, so the second
 * person's browser does not merely show the first person's rows for a moment,
 * it never asks the server at all. On a platform holding patients,
 * prescriptions and licences that is a data-separation failure, not a refresh
 * annoyance.
 *
 * It is called on **both** edges of the gap between two sessions, and one
 * alone would not do:
 *
 * - when a session ends, so another tenant's rows are not left in memory on a
 *   machine nobody is signed in to;
 * - when a session begins, because anything fetched in between — a request of
 *   the old session's that resolved late, a refetch from a component still
 *   mounted — was written after the first clear and belongs to nobody.
 *
 * What it deliberately does not touch: the device's own preferences (colour
 * mode, density, sidebar, paper size) and the stored interface language. The
 * language is the subtle one — the new user's `preferredLocale` is reapplied
 * by the auth store the moment their session opens, so they get their own
 * language, not the previous user's.
 */
export function forgetSessionState() {
  queryClient.clear();
}
